import { Comment, VideoProgress, ViewHistory, User, Category, Video, Module, SupportFile } from '@/types';
import { api } from '@/lib/api';
import { getCurrentUser } from '@/lib/auth';

// Helpers de conversão DB -> UI
const mapCategory = (row: any): Category => ({
  id: row.id,
  name: row.name,
  description: row.description || '',
  thumbnail: row.thumbnail || undefined,
  createdAt: row.created_at ? new Date(row.created_at) : new Date(),
  releaseAt: row.release_at ? new Date(row.release_at) : null,
});

const mapVideo = (row: any): Video => ({
  id: row.id,
  title: row.title,
  description: row.description || '',
  videoUrl: row.video_url || row.videoUrl || '',
  thumbnail: row.thumbnail || row.thumb || undefined,
  categoryId: row.category_id || row.categoryId,
  categoryIds: row.category_ids || row.categoryIds || undefined,
  duration: row.duration || 0,
  uploadedBy: row.uploaded_by || row.uploadedBy || 'admin',
  uploadedAt: row.uploaded_at ? new Date(row.uploaded_at) : (row.uploadedAt ? new Date(row.uploadedAt) : new Date()),
  // Só leitura: toDbVideo não devolve este campo, para uma edição de outro
  // atributo não regravar uma ordem desatualizada.
  order: Number(row.order ?? 0),
  vimeoId: row.vimeo_id || row.vimeoId || undefined,
  vimeoEmbedUrl: row.vimeo_embed_url || row.vimeoEmbedUrl || undefined,
  moduleId: row.module_id || row.moduleId || undefined,
  supportFiles: Array.isArray(row.support_files || row.supportFiles) ? (row.support_files || row.supportFiles) : [],
  releaseAt: row.release_at ? new Date(row.release_at) : (row.releaseAt ? new Date(row.releaseAt) : null),
  contentType: row.content_type || row.contentType || undefined,
  hasForm: !!(row.has_form ?? row.hasForm),
  formUrl: row.form_url ?? row.formUrl ?? null,
  formFile: row.form_file ?? row.formFile ?? null,
  mentorName: row.mentor_name ?? row.mentorName ?? null,
  mentorBio: row.mentor_bio ?? row.mentorBio ?? null,
  mentorPhoto: row.mentor_photo ?? row.mentorPhoto ?? null,
});

const toDbVideo = (v: Video) => ({
  title: v.title,
  description: v.description,
  video_url: v.videoUrl,
  thumbnail: v.thumbnail,
  category_id: v.categoryId,
  category_ids: v.categoryIds,
  duration: v.duration,
  uploaded_by: v.uploadedBy,
  vimeo_id: v.vimeoId,
  vimeo_embed_url: v.vimeoEmbedUrl,
  module_id: v.moduleId,
  support_files: v.supportFiles || [],
  release_at: v.releaseAt ? new Date(v.releaseAt).toISOString() : null,
  content_type: v.contentType,
  has_form: !!v.hasForm,
  form_url: v.hasForm ? (v.formUrl || null) : null,
  form_file: v.hasForm ? (v.formFile || null) : null,
  mentor_name: v.mentorName || null,
  mentor_bio: v.mentorBio || null,
  mentor_photo: v.mentorPhoto || null,
});

const mapUser = (row: any): User => ({
  id: row.id,
  name: row.name,
  email: row.email,
  role: (row.role as 'admin' | 'user' | 'cliente') || 'user',
  assignedCategories: row.assigned_categories || [],
  assignedModules: row.assigned_modules || [],
  assignedVideos: row.assigned_videos || [],
  createdAt: row.created_at ? new Date(row.created_at) : new Date(),
  isActive: row.is_active,
  userGroup: row.user_group ?? null,
});

// Comments
export const getComments = (videoId: string): Promise<Comment[]> =>
  api.getComments(videoId).then((rows: any[]) =>
    rows.map((r) => ({
      id: r.id,
      videoId: r.video_id,
      userId: r.user_id,
      userName: r.user_name || '',
      content: r.content,
      createdAt: r.created_at ? new Date(r.created_at) : new Date(),
    }))
  );

export const addComment = async (comment: Omit<Comment, 'id' | 'createdAt'>): Promise<Comment> => {
  const row = await api.addComment({
    video_id: comment.videoId,
    user_id: comment.userId,
    content: comment.content,
  });
  return {
    id: row.id,
    videoId: row.video_id,
    userId: row.user_id,
    userName: row.user_name || '',
    content: row.content,
    createdAt: row.created_at ? new Date(row.created_at) : new Date(),
  };
};

export const deleteComment = async (id: string): Promise<void> => {
  await api.deleteComment(id);
};

// Video Progress
export const getVideoProgress = async (videoId: string): Promise<VideoProgress | null> => {
  const user = getCurrentUser();
  if (!user) return null;
  const row = await api.getVideoProgress(user.id, videoId);
  if (!row) return null;
  return {
    videoId: row.video_id,
    currentTime: Math.max(0, row.time_watched || 0),
    duration: Math.max(row.time_watched || 0, row.duration || 0),
    completed: !!row.completed,
  };
};

export const saveVideoProgress = async (progress: VideoProgress) => {
  const user = getCurrentUser();
  if (!user) return;
  await api.saveVideoProgress({
    user_id: user.id,
    video_id: progress.videoId,
    time_watched: Math.floor(Math.max(0, progress.currentTime)),
    duration: Math.floor(Math.max(0, progress.duration, progress.currentTime)),
    completed: !!progress.completed,
  });
};

// Users (Profiles)
export const getUsers = async (): Promise<User[]> => {
  const rows = await api.getProfiles();
  return rows.map(mapUser);
};

export const addUser = async (user: User, password?: string): Promise<void> => {
  await api.addProfile({
    email: user.email,
    name: user.name,
    role: user.role,
    assigned_categories: user.assignedCategories,
    assigned_modules: user.assignedModules || [],
    assigned_videos: user.assignedVideos || [],
    is_active: user.isActive !== false,
    user_group: user.userGroup || null,
    password: password || undefined,
  });
};

export const updateUser = async (updatedUser: User): Promise<void> => {
  await api.updateProfile(updatedUser.id, {
    email: updatedUser.email,
    name: updatedUser.name,
    role: updatedUser.role,
    assigned_categories: updatedUser.assignedCategories,
    assigned_modules: updatedUser.assignedModules || [],
    assigned_videos: updatedUser.assignedVideos || [],
    is_active: updatedUser.isActive !== false,
    user_group: updatedUser.userGroup || null,
  });
};

export const deleteUser = async (_userId: string): Promise<void> => {
  await api.deleteProfile(_userId);
};

// Categories
export const getCategories = async (): Promise<Category[]> => {
  const rows = await api.getCategories();
  return rows.map(mapCategory);
};

export const addCategory = async (category: Category): Promise<void> => {
  await api.addCategory({
    name: category.name,
    description: category.description,
    thumbnail: category.thumbnail,
    release_at: category.releaseAt ? new Date(category.releaseAt).toISOString() : null,
  });
};

export const updateCategory = async (updatedCategory: Category): Promise<void> => {
  await api.updateCategory(updatedCategory.id, {
    name: updatedCategory.name,
    description: updatedCategory.description,
    thumbnail: updatedCategory.thumbnail,
    release_at: updatedCategory.releaseAt ? new Date(updatedCategory.releaseAt).toISOString() : null,
  });
};

export const deleteCategory = async (categoryId: string): Promise<void> => {
  await api.deleteCategory(categoryId);
};

// Videos
export const getVideos = async (): Promise<Video[]> => {
  const rows = await api.getVideos();
  return rows.map(mapVideo);
};

export const addVideo = async (video: Video): Promise<void> => {
  await api.addVideo(toDbVideo(video));
};

export const updateVideo = async (updatedVideo: Video): Promise<void> => {
  await api.updateVideo(updatedVideo.id, toDbVideo(updatedVideo));
};

export const deleteVideo = async (videoId: string): Promise<void> => {
  await api.deleteVideo(videoId);
};

// View History
export const getViewHistory = async (userId?: string): Promise<ViewHistory[]> => {
  const rows = await api.getViewHistory(userId);
  return rows.map((r: any) => ({
    id: r.id,
    userId: r.user_id,
    videoId: r.video_id,
    watchedDuration: r.watched_duration || 0,
    completed: !!r.completed,
    lastWatchedAt: r.last_watched_at ? new Date(r.last_watched_at) : new Date(),
  }));
};

export const getRecentViews = async (limit = 10) => {
  const rows = await api.getRecentViews(limit);
  return rows.map((r: any) => ({
    id: r.id,
    userId: r.user_id,
    userName: r.user_name,
    videoId: r.video_id,
    videoTitle: r.video_title,
    videoThumbnail: r.video_thumbnail,
    videoVimeoId: r.video_vimeo_id,
    videoUrl: r.video_url,
    videoCategoryId: r.video_category_id,
    watchedDuration: r.watched_duration || 0,
    completed: !!r.completed,
    lastWatchedAt: r.last_watched_at ? new Date(r.last_watched_at) : new Date(),
  }));
};

export const addToHistory = async (history: Omit<ViewHistory, 'id'>): Promise<ViewHistory> => {
  const row = await api.addToHistory({
    user_id: history.userId,
    video_id: history.videoId,
    watched_duration: history.watchedDuration,
    completed: history.completed,
  });
  return {
    id: row.id,
    userId: row.user_id,
    videoId: row.video_id,
    watchedDuration: row.watched_duration || 0,
    completed: !!row.completed,
    lastWatchedAt: row.last_watched_at ? new Date(row.last_watched_at) : new Date(),
  };
};

// Settings helpers
export const getWelcomeVideo = async (role: 'user' | 'cliente') => {
  const key = role === 'cliente' ? 'welcome_video_cliente' : 'welcome_video_user';
  return await api.getSetting(key);
};

export const setWelcomeVideo = async (role: 'user' | 'cliente', value: { title?: string; url?: string; vimeo_id?: string }) => {
  const key = role === 'cliente' ? 'welcome_video_cliente' : 'welcome_video_user';
  return await api.setSetting(key, value);
};

export const uploadSupportFile = async (file: File) => {
  const allowed = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'text/plain',
    'text/csv',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ];
  if (!allowed.includes(file.type)) throw new Error('Formato não suportado');
  const b64 = await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error('Falha ao ler arquivo'));
    r.readAsDataURL(file);
  });
  const res = await api.uploadFile({ filename: file.name, mimeType: file.type, dataBase64: b64 });
  return res as SupportFile;
};

// Modules
const mapModule = (row: any): Module => ({
  id: row.id,
  categoryId: row.category_id,
  parentId: row.parent_id,
  title: row.title,
  description: row.description || undefined,
  order: row.order || 0,
  createdAt: row.created_at ? new Date(row.created_at) : undefined,
  updatedAt: row.updated_at ? new Date(row.updated_at) : undefined,
  releaseAt: row.release_at ? new Date(row.release_at) : null,
  evaluationUrl: row.evaluation_url || null,
});

export const getModules = async (categoryId?: string): Promise<Module[]> => {
  const rows = await api.getModules(categoryId);
  return rows.map(mapModule);
};

/** Busca módulos de várias categorias numa chamada só e devolve agrupado. */
export const getModulesByCategories = async (categoryIds: string[]): Promise<Record<string, Module[]>> => {
  const map: Record<string, Module[]> = {};
  for (const id of categoryIds) map[id] = [];
  if (categoryIds.length === 0) return map;
  const rows = await api.getModulesFor(categoryIds);
  for (const row of rows) {
    const mod = mapModule(row);
    if (!map[mod.categoryId]) map[mod.categoryId] = [];
    map[mod.categoryId].push(mod);
  }
  return map;
};

export const addModule = async (module: Omit<Module, 'id' | 'createdAt' | 'updatedAt' | 'children'>): Promise<Module> => {
  const row = await api.addModule({
    category_id: module.categoryId,
    parent_id: module.parentId || null,
    title: module.title,
    description: module.description,
    order: module.order,
    release_at: module.releaseAt ? new Date(module.releaseAt).toISOString() : null,
    evaluation_url: module.evaluationUrl || null,
  });
  return mapModule(row);
};

export const updateModule = async (id: string, updates: Partial<Omit<Module, 'id' | 'createdAt' | 'updatedAt' | 'children'>>): Promise<Module> => {
  const row = await api.updateModule(id, {
    category_id: updates.categoryId,
    parent_id: updates.parentId,
    title: updates.title,
    description: updates.description,
    order: updates.order,
    release_at: updates.releaseAt ? new Date(updates.releaseAt).toISOString() : updates.releaseAt === null ? null : undefined,
    evaluation_url: updates.evaluationUrl === undefined ? undefined : (updates.evaluationUrl || null),
  });
  return mapModule(row);
};

export const deleteModule = async (id: string): Promise<void> => {
  await api.deleteModule(id);
};

/**
 * Quantos dias um conteúdo permanece marcado como "Novo" desde a publicação.
 * O documento da MICRO pede que esse prazo seja definido e informado ao usuário;
 * antes eram 7 dias fixos no código, sem nada explicando.
 */
export const DIAS_NOVIDADE_PADRAO = 7;

export const getDiasNovidade = async (): Promise<number> => {
  const bruto = await api.getSetting('dias_novidade').catch(() => null);
  const n = Number((bruto as { dias?: unknown } | null)?.dias);
  return Number.isFinite(n) && n > 0 && n <= 365 ? Math.floor(n) : DIAS_NOVIDADE_PADRAO;
};

export const setDiasNovidade = async (dias: number) =>
  api.setSetting('dias_novidade', { dias: Math.max(1, Math.min(365, Math.floor(dias))) });
